/****************
 * IS THIS TRADER A BULL OR A BEAR? THE ANSWER IS WHERE THEY MAKE MONEY.
 *
 * Guillaume, 2026-09-22: "The Bull/Bear bar is useless, it's just a long/short
 * ratio that we already display in a donut, I taught we were going to make a
 * real assessment of the trader being a bull or a bear."
 *
 * He is right. A bar showing 70% long beside a donut showing 70% long is one
 * fact drawn twice, and it is not the fact anybody wants. How OFTEN somebody
 * goes long says nothing about whether they are any good at it -- a trader can
 * be 80% long and make every dollar of their profit on the other 20%.
 *
 * So this splits REALISED PnL by direction. "Bull" means their longs made
 * money and their shorts did not. That is a claim about skill, and the two
 * numbers behind it are shown beside the word so it can be checked.
 *
 * ── Why not net exposure, and why not market correlation ────────────────────
 *
 * Net exposure says what they hold RIGHT NOW, which is a position rather than
 * a disposition -- that is the Net Position column. Correlating their PnL
 * against BTC's move over each trade window would be truer still, and it needs
 * candles joined to every trade; worth doing, not this.
 *
 * ── The verdicts, and why there are four ────────────────────────────────────
 *
 * Two sides, each profitable or not, is four states and all four are real:
 * a trader who makes money both ways is not a bull, and one who loses both
 * ways is not a bear. Collapsing those into "whichever is bigger" would print
 * "Bull" for somebody losing money in both directions, which is the kind of
 * confident nonsense this replaces.
 ****************/

import i18n from '@/i18n';
import { fmtFixed, fmtUsd, ltr } from '@/lib/format';

export type EdgeVerdict = 'bull' | 'bear' | 'both' | 'neither' | 'unknown';

/** Closed trades (realised, after fees) or open positions (unrealised). */
export type EdgeKind = 'closed' | 'open';

export interface DirectionalEdge {
  verdict: EdgeVerdict;
  longPnl: number;
  shortPnl: number;
  longTrades: number;
  shortTrades: number;
  kind: EdgeKind;
  /** The sentence shown to a reader. Never longer than a tile. English (the
   *  language loaded when it was computed); render `labelKey` with t(). */
  label: string;
  /** Translation key of `label` (namespace included), for t(). */
  labelKey: string;
}

/** Realised PnL, split by direction, over CLOSED trades only. */
export function directionalEdge(trades: unknown[]): DirectionalEdge {
  let longPnl = 0;
  let shortPnl = 0;
  let longTrades = 0;
  let shortTrades = 0;

  for (const raw of trades ?? []) {
    const t = raw as Record<string, unknown>;
    const dir = String(t.direction ?? t.side ?? '').toUpperCase();
    // After fees and funding when the row says so (our own trades do).
    const n = Number(t.net_pnl_usd ?? t.pnl_usd ?? t.realized_pnl ?? t.closing_pnl ?? 0);
    if (!Number.isFinite(n)) continue;
    if (dir.startsWith('LONG')) {
      longPnl += n;
      longTrades++;
    } else if (dir.startsWith('SHORT')) {
      shortPnl += n;
      shortTrades++;
    }
  }

  return edgeFromTotals(longPnl, shortPnl, longTrades, shortTrades);
}

/**
 * The same verdict from totals a server already summed -- the Dashboard's
 * closed history arrives aggregated, with no trade list to walk. 2026-09-25.
 */
export function edgeFromTotals(
  longPnl: number, shortPnl: number, longTrades: number, shortTrades: number,
  kind: EdgeKind = 'closed',
): DirectionalEdge {
  const verdict: EdgeVerdict = (() => {
    // Nothing closed on either side is not a verdict, it is an absence.
    if (longTrades === 0 && shortTrades === 0) return 'unknown';
    const longWins = longPnl > 0;
    const shortWins = shortPnl > 0;
    if (longWins && shortWins) return 'both';
    if (longWins) return 'bull';
    if (shortWins) return 'bear';
    return 'neither';
  })();

  const labelKey = edgeSentence(verdict, kind, longTrades, shortTrades, longPnl, shortPnl);
  return {
    verdict, longPnl, shortPnl, longTrades, shortTrades, kind,
    label: i18n.t(labelKey),
    labelKey,
  };
}

/** Open positions right now, by unrealised PnL: the Live view's question. */
export function openEdge(positions: unknown[]): DirectionalEdge {
  let longPnl = 0, shortPnl = 0, longTrades = 0, shortTrades = 0;
  for (const raw of positions ?? []) {
    const p = raw as Record<string, unknown>;
    const dir = String(p.direction ?? p.side ?? '').toUpperCase();
    const n = Number(p.unrealized_pnl ?? p.pnl_usd ?? 0);
    if (!Number.isFinite(n)) continue;
    if (dir.startsWith('LONG') || dir === 'BUY') { longPnl += n; longTrades++; }
    else if (dir.startsWith('SHORT') || dir === 'SELL') { shortPnl += n; shortTrades++; }
  }
  return edgeFromTotals(longPnl, shortPnl, longTrades, shortTrades, 'open');
}

/****************
 * ONE PLAIN SENTENCE, NOT A LABEL. Guillaume, 2026-09-25: "Bear -- makes
 * money short" beside a donut showing 69% of his volume and 80% of his trades
 * LONG "is just wrong" and took him minutes to decode. What it meant: most
 * trades were long, yet the profit came from the shorts. So it says exactly
 * that, reading the trade mix and the results together, with no bull/bear
 * word that contradicts the ring above it.
 ****************/
export function edgeSentence(
  v: EdgeVerdict, kind: EdgeKind,
  longTrades: number, shortTrades: number, longPnl: number, shortPnl: number,
): string {
  // Returns the translation KEY of the sentence (traders namespace); each
  // sentence is whole in the locale files so no language has to assemble it.
  if (v === 'unknown') return kind === 'open' ? 'traders:edge.nothingOpen' : 'traders:edge.noClosed';
  // One side never traded: say ALL, not "most" (Guillaume, 2026-09-25:
  // 7 longs and 0 shorts read "Most trades are long").
  if (longTrades === 0 || shortTrades === 0) {
    const side = longTrades > 0 ? 'long' : 'short';
    const up = (longTrades > 0 ? longPnl : shortPnl) > 0;
    return kind === 'open'
      ? `traders:edge.allOpen.${side}.${up ? 'profit' : 'loss'}`
      : `traders:edge.all.${side}.${up ? 'made' : 'lost'}`;
  }
  if (kind === 'open') {
    if (v === 'both') return 'traders:edge.open.both';
    if (v === 'neither') return 'traders:edge.open.neither';
    return v === 'bull'
      ? 'traders:edge.open.bull'
      : 'traders:edge.open.bear';
  }
  if (v === 'both') {
    return `traders:edge.both.${longPnl >= shortPnl ? 'longs' : 'shorts'}`;
  }
  if (v === 'neither') return 'traders:edge.neither';
  const winner = v === 'bull' ? 'long' : 'short';
  if (longTrades === shortTrades) return `traders:edge.even.${winner}`;
  const most = longTrades > shortTrades ? 'long' : 'short';
  return most === winner
    ? `traders:edge.mostWon.${most}`
    : `traders:edge.mostLost.${most}`;
}

/** Compact money, for a figure that sits inside a chart card. */
export function shortUsd(v: number): string {
  const sign = v < 0 ? '-' : '+';
  const a = Math.abs(v);
  // ltr(): the sign stays in front of the amount in a right-to-left page.
  if (a >= 1_000_000) return ltr(`${sign}$${fmtFixed(a / 1_000_000, 1)}M`);
  if (a >= 1_000) return ltr(`${sign}$${fmtFixed(a / 1_000, 1)}k`);
  return ltr(`${sign}${fmtUsd(a, 2, { group: false })}`);
}
